import { Injectable } from '@angular/core';
import {
  BooleanCons,
  MinShippingPromoCodeAmount,
  NumberCons,
  Operation,
  StringConst,
} from '../constants/const';
import { Container } from '../model/http/container-selector.model';
import { Iinvoice } from '../model/http/invoices';
import { CallPlace, DisplayPrices, IProducts, PricesPieces, ProductAttributes } from '../model/http/myActivity.model';
import { SuccessPayMethod } from '../model/http/ordersresume.model';
import { ChangePaymentMethod } from '../model/http/paymentMethod.model';
import { IShoppingList, ShippingDiscountType } from '../model/http/shoppingList';
import {
  GeneralTotalDescription,
  TotalDescriptionShopping,
} from '../model/local/general.model';
import { PaymentInformation } from '../model/local/payment.model';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  constructor(private commonService: CommonService) { }

  getIvaOfProducts(price: number, iva: number): number {
    return this.commonService.calculateTernary(
      iva === NumberCons.zero,
      iva,
      price * (iva / 100)
    );
  }

  getTaxRateValue(taxRate: number): number {
    return this.commonService.calculateTernary(
      taxRate === NumberCons.zero,
      NumberCons.zero,
      taxRate / 100
    );
  }

  updateTotalDescriptionCalculated(
    totalDescription: TotalDescriptionShopping,
    generalData: GeneralTotalDescription
  ): TotalDescriptionShopping {
    totalDescription.productTotal = generalData.totalProducts;
    totalDescription.piecesTotal = generalData.totalPices;
    totalDescription.subtotal = generalData.subTotal;
    totalDescription.tax = generalData.generalTax;
    totalDescription.costShipping = generalData.costShipping;
    totalDescription.total =
      totalDescription.costShipping +
      totalDescription.subtotal +
      totalDescription.tax - (totalDescription.promotionalCodeAmount ?? 0);
    totalDescription.totalUtilities = generalData.totalUtilities ?? 0;
    totalDescription.totaldiscount = generalData.totaldiscount;
    totalDescription.total =
      this.commonService.calculateTernary(
        totalDescription.shippingCostDiscountPercent !== 0,
        this.commonService.calculatePriceWithDiscount(totalDescription.costShipping, totalDescription.shippingCostDiscountPercent),
        totalDescription.costShipping
      ) +
      totalDescription.subtotal +
      totalDescription.tax -
      totalDescription.totaldiscount - (totalDescription.promotionalCodeAmount ?? 0);
    return totalDescription;
  }

  calculateContainerIVA(container: Container): number {
    return (
      container.price +
      container.price * this.getTaxRateValue(container.taxRate)
    );
  }
  calculateUtilitiesPercentage(cost: number, costPublic: number): number {
    if (cost >= costPublic) {
      return 0;
    }
    const utilitiesPercentage = (1 - cost / costPublic) * 100;
    return Math.round(utilitiesPercentage * 100) / 100;
  }
  calculateCostPublic(cost: number, utilitiesPercentage: number): number {
    const costPublic = cost / (1 - utilitiesPercentage / 100);
    return Math.round(costPublic * 100) / 100;
  }

  calculateUtility(cost: number, costPublic: number): number {
    return this.commonService.calculateTernary(
      cost >= costPublic,
      NumberCons.zero,
      costPublic - cost
    );
  }
  calculateAmountUtility(pieces: number, utility: number): number {
    return pieces * utility;
  }
  updateTotalUtilities(
    totalUtilities: number,
    oldUtilityAmount: number,
    newUtilities: number
  ): number {
    return totalUtilities - oldUtilityAmount + newUtilities;
  }
  getOnlyTwoDecimals(numberFull: number): number {
    return Math.round(numberFull * 100) / 100;
  }
  getGoalPercent(totalAmount: number, goal: number): number {
    return this.commonService.calculateTernary(
      goal <= NumberCons.zero,
      NumberCons.zero,
      totalAmount / (goal / 100)
    );
  }
  getDiscountTotal(total: number, percent: number): number {
    return total * (percent / 100);
  }

  getEffectiveShippingCost(shippingCost: number, discountType: ShippingDiscountType, discountValue: number): number {
    if (!shippingCost) {
      return NumberCons.zero;
    }
    if (discountType === ShippingDiscountType.monto) {
      return discountValue >= shippingCost
        ? MinShippingPromoCodeAmount
        : shippingCost - discountValue;
    }
    return this.commonService.calculatePriceWithDiscount(shippingCost, discountValue ?? NumberCons.zero);
  }

  getCartTotalWithShipping(response: IShoppingList, shippingCost: number): number {
    if (!response) {
      return NumberCons.zero;
    }
    const shippingAdjusted = this.getEffectiveShippingCost(
      shippingCost ?? NumberCons.zero,
      response.shippingDiscountType,
      response.shippingDiscountValue,
    );
    return this.getOnlyTwoDecimals(
      shippingAdjusted
      + (response.tax ?? NumberCons.zero)
      + (response.subTotal ?? NumberCons.zero)
      - (response.discount ?? NumberCons.zero)
      - (response.promotionalCodeAmount ?? NumberCons.zero),
    );
  }

  getSuccessPayMethod(data?: ChangePaymentMethod): SuccessPayMethod {
    const changeMethod = data ?? new ChangePaymentMethod();
    const totalDescription = new TotalDescriptionShopping();
    totalDescription.subtotal = changeMethod.subtotal;
    totalDescription.total = changeMethod.totalCost;
    totalDescription.tax = changeMethod.iva;
    totalDescription.costShipping = changeMethod.costShipping;
    totalDescription.totaldiscount = changeMethod.totalDiscount;
    totalDescription.isHideTotalsHeader = BooleanCons.true;
    totalDescription.fromCalled = CallPlace.successPay;
    totalDescription.hasDiscountSpecial = changeMethod.hasDiscountSpecial;
    totalDescription.percentDiscountSpecial = changeMethod.percentDiscountSpecial;
    totalDescription.discountSpecial = changeMethod.discountSpecialAmount;
    totalDescription.totalWithSpecialDiscount = totalDescription.total;
    totalDescription.hasFavorBalance = changeMethod.creditNoteAmount > 0;
    totalDescription.favorBalance = changeMethod.creditNoteAmount;
    totalDescription.total += changeMethod.creditNoteAmount;
    totalDescription.totalWithSpecialDiscount += changeMethod.creditNoteAmount;
    totalDescription.promotionalCodeAmount = changeMethod.promotionalCodeAmount ?? 0;
    totalDescription.shippingCostDiscountPercent = this.calculatePercentageOf(changeMethod.costShipping, changeMethod.costShippingDiscount);
    return {
      totalDescription,
      callPlace: CallPlace.pendingOrders,
      shippingCostAccept: changeMethod.isShippingCostAccepted,
      data: data?.paymentMethod ?? new PaymentInformation(),
    };
  }

  calculatePercentageOf(completeValue: number, percentageValue: number): number {
    const percent = this.commonService.validateAndValue([completeValue !== 0])
      ? ((completeValue - percentageValue) / completeValue) * 100
      : 0;
    return percent;
  }

  calculateProductPrices(product: IProducts): IProducts {
    const decimalTax = this.getTaxRateValue(product.taxRate);
    const taxDiscount = decimalTax * product.costPerPieceDiscount;
    const tax = decimalTax * product.costPerPiece;
    const displayPrices = product.displayPrices ?? new DisplayPrices();
    displayPrices.costPerPieceDiscount = taxDiscount + product.costPerPieceDiscount;
    displayPrices.costPerPiece = tax + product.costPerPiece;
    displayPrices.taxRateCalculated = tax;
    displayPrices.taxRateDiscountCalculated = taxDiscount;
    product.utilityPercentage = this.calculateUtilitiesPercentage(
      displayPrices.costPerPieceDiscount,
      product.priceToPublic
    );
    product.displayPrices = displayPrices;
    return product;
  }
  calculateContainersPrices(product: IProducts, containers: ProductAttributes[]): ProductAttributes[] {
    containers.forEach((container) => {
      const decimalTax = this.getTaxRateValue(container.containerTaxRate);
      const taxDiscount = decimalTax * container.containerCostDiscount;
      const tax = decimalTax * container.containerCost;
      const displayPrices = container.displayPrices ?? new DisplayPrices();
      const productPrices = product.displayPrices ?? new DisplayPrices();
      displayPrices.costPerPiece = (container.containerCost + tax);
      displayPrices.costPerPieceDiscount = (container.containerCostDiscount + taxDiscount);
      displayPrices.taxRateCalculated = tax;
      displayPrices.taxRateDiscountCalculated = taxDiscount;
      displayPrices.total = (displayPrices.costPerPieceDiscount
        + productPrices.costPerPieceDiscount) * container.quantity;
      container.displayPrices = displayPrices;
    });
    return containers;
  }

  calculateTotal(containers: ProductAttributes[]): number {
    return containers.reduce((total, container) => total + container.displayPrices.total, 0);
  }

  calculateCostTotalProduct(containers: number[]): number {
    return containers.reduce((total, container) => total + container, 0);
  }

  calculateContainerPricesDiscounts(
    pricePieces: PricesPieces[],
    attributes: ProductAttributes[],
    taxRateProduct: number): ProductAttributes[] {
    const productDecimalTax = this.getTaxRateValue(taxRateProduct);
    attributes.forEach((attribute, index) => {
      attribute.displayProductPrices = new DisplayPrices();
      const productTax = pricePieces[index].costPerPiece * productDecimalTax;
      const productDiscountTax = pricePieces[index].priceWithDiscount * productDecimalTax;

      const containerDecimalTax = this.getTaxRateValue(attribute.containerTaxRate);
      const containerTax = attribute.containerCost * containerDecimalTax;
      const containerDiscountTax = attribute.containerCostDiscount * containerDecimalTax;

      const productCost = pricePieces[index].costPerPiece + productTax;
      const productDiscountCost = pricePieces[index].priceWithDiscount + productDiscountTax;
      const containerCost = attribute.containerCost + containerTax;
      const containerDiscountCost = attribute.containerCostDiscount + containerDiscountTax;

      attribute.displayProductPrices.costPerPiece = (productCost + containerCost) * attribute.quantity;
      attribute.displayProductPrices.costPerPieceDiscount = (productDiscountCost + containerDiscountCost) * attribute.quantity;
    });
    return attributes;
  }

  calculateQuantityPiecesByProduct(product: IProducts): number {
    if (!product?.productAttributes) { return 0; }
    return product.productAttributes.reduce((quantity, attribute) => quantity + attribute.quantity, 0);
  }
  getDiferenceBetweenTwoPrices(price: number, priceWithDiscount: number): number {
    return price - priceWithDiscount;
  }
  getProductTotals(products: IProducts[]): TotalDescriptionShopping {
    const productsNoRef: IProducts[] = Object.assign([], [...products]);
    const totalDescription = new TotalDescriptionShopping();
    totalDescription.productTotal = productsNoRef.length;
    productsNoRef.forEach(product => {

      product = this.calculateProductPrices(product);
      product.productAttributes = this.calculateContainersPrices(product, product.productAttributes);
      product.productAttributes.forEach(attribute => {
        totalDescription.tax += (product.displayPrices.taxRateDiscountCalculated +
          attribute.displayPrices.taxRateDiscountCalculated) * attribute.quantity;
        totalDescription.subtotal += ((product.costPerPiece + attribute.containerCost) * attribute.quantity);
        totalDescription.totaldiscount += (this.getDiferenceBetweenTwoPrices(attribute.containerCost,
          attribute.containerCostDiscount) *
          attribute.quantity);
        totalDescription.totaldiscount += (this.getDiferenceBetweenTwoPrices(product.costPerPiece,
          product.costPerPieceDiscount) *
          attribute.quantity);
        totalDescription.piecesTotal += attribute.quantity;
      });
    });
    totalDescription.total = totalDescription.subtotal + totalDescription.tax - totalDescription.totaldiscount;
    return totalDescription;
  }

  getTotalPiecesByProduct(product: IProducts): number {
    if (!product?.productAttributes) { return 0; }
    return product.productAttributes.reduce((total, attribute) => total + attribute.quantity, 0);
  }

  getTotalUtilities(products: IProducts[]): number {
    return products.map(product => product.utilityByProduct).
      reduce((totalUtility, item) => totalUtility + item, 0);
  }

  calculateIProductPrices(product: IProducts): IProducts {
    const resultProduct = { ...product };
    resultProduct.productAttributes =
      this.calculateContainersPrices(
        resultProduct,
        resultProduct.productAttributes
      );

    const decimalTax = this.getTaxRateValue(product.taxRate);
    resultProduct.productAttributes.forEach((x, index) => {
      const invalidLabels = (x.invalidLabels ?? []).map(y => y.toUpperCase());
      const price = this.commonService.calculateTernary(invalidLabels.includes((x.label ?? '').toUpperCase()),
        x.costPerPiece,
        x.priceWithDiscount);

      const taxDiscount = decimalTax * price;
      const tax = decimalTax * x.costPerPiece;
      const displayPrices = x.displayProductPrices ?? new DisplayPrices();

      displayPrices.costPerPieceDiscount = taxDiscount +
        price +
        resultProduct.productAttributes[index].displayPrices.costPerPieceDiscount;
      displayPrices.costPerPiece = tax +
        resultProduct.costPerPiece +
        resultProduct.productAttributes[index].displayPrices.costPerPieceDiscount;
      displayPrices.taxRateCalculated = tax;
      displayPrices.taxRateDiscountCalculated = taxDiscount;
      displayPrices.total = displayPrices.costPerPieceDiscount * resultProduct.productAttributes[index].quantity;

      x.displayProductPrices = displayPrices;
      x.displayProductPrices.total = displayPrices.costPerPieceDiscount * x.quantity;
      x.displayProductPrices.totalWithoutDiscount = displayPrices.costPerPiece * x.quantity;
    });

    const productTax = decimalTax * resultProduct.costPerPiece;
    const productTaxDiscount = decimalTax * resultProduct.costPerPieceDiscount;

    resultProduct.displayPrices = product.displayPrices ?? new DisplayPrices();
    resultProduct.displayPrices.costPerPieceDiscount = productTaxDiscount + resultProduct.costPerPieceDiscount;
    resultProduct.displayPrices.costPerPiece = productTax + resultProduct.costPerPiece;
    resultProduct.displayPrices.total = resultProduct.productAttributes.reduce((total, attr) =>
      total + (attr.quantity * attr.displayProductPrices.costPerPieceDiscount), 0);

    return resultProduct;
  }


}
